import * as azmaps from 'azure-maps-control';
import * as azmdraw from 'azure-maps-drawing-tools';
import { Utils } from '../helpers/Utils';

export enum SelectionControlMode {
    circle = 'circle',
    rectangle = 'rectangle',
    polygon = 'polygon'
}

export enum ShapeSelectionMode {
    point = 'point',
    line = 'line',
    polygon = 'polygon',
    any = 'any'
}

export interface SelectionControlOptions {
    container?: string | HTMLElement;
    style?: azmaps.ControlStyle;
    source?: azmaps.source.DataSource | azmaps.source.VectorTileSource;
    sourceLayer?: string;
    rangeSourceId?: string;
    selectionModes?: SelectionControlMode[] | 'all';
    fillColor?: string;
    fillOpacity?: number;
    strokeColor?: string;
    strokeWidth?: number;
    persistSearchArea?: boolean;
    shapeSelectionMode?: ShapeSelectionMode | string;
    drawingManager?: azmdraw.drawing.DrawingManager;
}

/** The events supported by the `SelectionControl`. */
export interface SelectionControlEvents {
    /** Event fired when shapes are selected from the specified data source. */
    dataselected: (azmaps.data.Feature<azmaps.data.Geometry, any> | azmaps.Shape)[];

    /** Event fired when a search area has been created. */
    searchareacreated: azmaps.data.Polygon | azmaps.data.MultiPolygon;
}

// @ts-ignore
/** A control that lets the user use different methods to select data from the map. */
// eslint-disable-next-line import/namespace
export class SelectionControl extends azmaps.internal.EventEmitter<SelectionControlEvents> implements azmaps.Control {


    private static readonly _css = {
        button: 'azure-maps-control-button',
        inUse: 'in-use',
        hidden: 'hidden-accessible-element',
    };

    private _options: SelectionControlOptions = {
        style: azmaps.ControlStyle.light,
        selectionModes: 'all',
        fillColor: '#F2C811',
        fillOpacity: 0.5,
        strokeColor: '#F2C811',
        strokeWidth: 1,
        persistSearchArea: false,
        shapeSelectionMode: 'any',
        rangeSourceId: 'selection-control-range',
    };

    _rangeDataSource: azmaps.source.DataSource;

    // @ts-ignore
    private _position: azmaps.ControlPosition = 'top-left';
    // @ts-ignore
    private _map: azmaps.Map;
    // @ts-ignore
    private _drawingManager: azmdraw.drawing.DrawingManager;
    // @ts-ignore
    private _container: HTMLElement;
    // @ts-ignore
    private _rangeLayers: (azmaps.layer.PolygonLayer | azmaps.layer.LineLayer)[];
    // @ts-ignore
    private _hasMouse = false;
    // @ts-ignore
    private _hasFocus = false;
    /**
     * A control that lets the user use different methods to select data from the map.
     * @param options Options for defining how the control is rendered and functions.
     */
    constructor(options?: SelectionControlOptions) {
        super();

        this._options = Object.assign(this._options, options || {});
        this._rangeDataSource = new azmaps.source.DataSource(this._options.rangeSourceId || 'selection-control-range', {
            buffer: 512,
        });
    }

    private _buildSelectModeBtn(name: string, btnClass: string): HTMLButtonElement  {
        const btn = <HTMLButtonElement>Utils.createElm('button', {
            attr: {
                type: 'button',
                title: name + ' selection',
            },
            class: [btnClass, name + '-selection'],
        });

        btn.onclick = () => {
            this.clear();
            if (!this._drawingManager) {
                return;
            }
            this._drawingManager.setOptions({
                mode: <azmdraw.drawing.DrawingMode>('draw-' + name),
            });
        };

        return btn;
    }

    public clear(): void {
        const source = this._drawingManager?.getSource();
        if (source) {
            source.clear();
        }
        if (this._drawingManager) {
            this._drawingManager.setOptions({ mode: azmdraw.drawing.DrawingMode.idle });
        }
        this._rangeDataSource?.clear();
        // const invokeEvent = (this as any)._invokeEvent;
        this._raiseEvent('dataselected', []);
    }

    private _createContainer(): void {

        //Cache variables and function for better minification.
        const opts = this._options;
        const css = SelectionControl._css;

        const container =  Utils.createElm('div', {
            class: ['azure-maps-control-container', opts.style || 'light'],
        });
        this._container = container;

        const selectionGrid = Utils.createElm('div', {
            class: [css.hidden],
        });

        let modes: SelectionControlMode[] = [SelectionControlMode.circle, SelectionControlMode.rectangle, SelectionControlMode.polygon];
        if(Array.isArray(opts.selectionModes)){
            modes = opts.selectionModes as SelectionControlMode[];
        }

        modes.forEach(mode => {
            const selectionBtn = this._buildSelectModeBtn(mode, css.button);
            selectionGrid.appendChild(selectionBtn);
        });

        container.addEventListener('mouseover', () => {
            this._hasMouse = true;
            container.classList.add(css.inUse);
            selectionGrid.classList.remove(css.hidden);
        });

        container.addEventListener('focusin', () => {
            this._hasFocus = true;
            container.classList.add(css.inUse);
            selectionGrid.classList.remove(css.hidden);
        });

        container.addEventListener('mouseleave', () => {
            this._hasMouse = false;
            if (!this._hasFocus) {
                container.classList.remove(css.inUse);
                selectionGrid.classList.add(css.hidden);
            }
        });

        container.addEventListener('focusout', (event) => {
            if (!(event.relatedTarget instanceof Node && container.contains(event.relatedTarget))) {
                this._hasFocus = false;
                if (!this._hasMouse) {
                    container.classList.remove(css.inUse);
                    selectionGrid.classList.add(css.hidden);
                }
            }
        });

        //Create the flyout button.
        const flyoutBtn = Utils.createElm('button', {
            class: [css.button, 'pointer-selection'],
            attr: {
                type: 'button',
                title: 'Selection',
            },
        });

        //If search area is persisted, remove it when the user clicks on the flyout button.
        flyoutBtn.addEventListener('click', () => {
            this.clear();
        });

        if (opts && (this._position === 'top-right' || this._position === 'bottom-right')) {
            container.appendChild(selectionGrid);
            container.appendChild(flyoutBtn);
        } else {
            container.appendChild(flyoutBtn);
            container.appendChild(selectionGrid);
        }
    }

    public onAdd(map: azmaps.Map, options?: azmaps.ControlOptions): HTMLElement {

        this._position = (options && options.position)? options.position : <azmaps.ControlPosition>'non-fixed';
        this._map = map;

        const opts = this._options;

        const dm = opts.drawingManager || new azmdraw.drawing.DrawingManager(map);
        this._drawingManager = dm;

        //Customize the drawing styles.
        // const l = dm.getLayers();
        // l.polygonLayer.setOptions({
        //     fillColor: opts.fillColor,
        //     fillOpacity: opts.fillOpacity,
        // });
        //
        // const lineOptions = {
        //     strokeColor: opts.strokeColor,
        //     strokeWidth: opts.strokeWidth,
        // };
        //
        // l.polygonOutlineLayer.setOptions(lineOptions);
        // l.lineLayer.setOptions(lineOptions);

        this._rangeLayers = [];

        //Create the main control container.
        this._createContainer();

        //If a container is specified, move it to the container once the map has loaded the control.
        if(opts.container) {
            setTimeout(()=> {
                const c = (typeof opts.container === 'string')? document.getElementById(opts.container): opts.container;
                if (c) c.appendChild(this._container);
            }, 10);
        }

        return this._container;
    }

    onRemove(): void {
        if (!this._map) {
            return;
        }
        this._map.events.remove('drawingcomplete', this._drawingManager, this._searchArea);
        this._map.events.remove('drawingmodechanged', this._drawingManager, this._drawingModeChanged);
        if (this._rangeLayers && this._rangeLayers.length > 0) {
            this._map.layers.remove(this._rangeLayers);
        }
        if (this._container?.parentElement) {
            this._container.parentElement.removeChild(this._container);
        }
    }

    /****************************
     * Public Methods
     ***************************/

    /**
     * Updates the options of the selection control.
     * @param options The options to update.
     */
    public setOptions(options: SelectionControlOptions): void {
        this._options = Object.assign(this._options, options);
    }

    private _drawingModeChanged = (mode: any): void => {
        const modeName = typeof mode === 'string' ? mode : mode?.mode || mode?.newMode || '';
        if (!modeName || String(modeName).indexOf('draw-') === 0) {
            this.clear();
        }
    };

    private _searchArea = (e: any): void => {
        const shape = e?.shape ?? e?.shapes?.[0] ?? e;
        if (!shape) {
            return;
        }
        const polygon = Utils.getPolygon(shape);
        if (!polygon) {
            return;
        }
        if (this._drawingManager) {
            this._drawingManager.getSource().clear();
        }
        this._rangeDataSource.setShapes([polygon]);

        // const invokeEvent = (this as any)._invokeEvent;
        this._raiseEvent('searchareacreated', polygon);

        this._raiseEvent('dataselected', []);
    };

    private _raiseEvent(eventName: keyof SelectionControlEvents, payload: any): void {
        const emitter = this as any;
        if (typeof emitter._invokeEvent === 'function') {
            emitter._invokeEvent(eventName, payload);
            return;
        }
        if (typeof emitter._emit === 'function') {
            emitter._emit(eventName, payload);
            return;
        }
        if (typeof emitter.emit === 'function') {
            emitter.emit(eventName, payload);
        }
    }
}
